function defaultMatcher(text: string, options: Options): Options {
	if (!text) {
		return options;
	}

	const fragments = text.toLowerCase().split(/\s+/g);
	if (fragments.length === 1) { // Make the trivial (and most common) case faster
		return options.filter(option => option.toLowerCase().includes(fragments[0]));
	}

	return options.filter(option => {
		const lcOption = option.toLowerCase();
		let position = 0;
		for (const fragment of fragments) {
			const index = lcOption.indexOf(fragment, position);
			if (index === -1) {
				return false;
			}
			position = index + fragment.length;
		}
		return true;
	});
}

export type Options = string[];
export default class AccessibleAutocomplete {
	private readonly fieldArea: HTMLFieldSetElement;
	private readonly label: HTMLLabelElement;
	private readonly results: HTMLUListElement;
	private readonly announcementArea: HTMLElement;
	private readonly optionIdPrefix: string;

	private readonly updateQueue: {line: Promise<void>, position: number, lastValue?: string} = {line: Promise.resolve(), position: 0, lastValue: undefined};

	private currentlyConsideredElement?: HTMLElement = undefined;
	private loading = false;
	private trackingMouse = false;
	private eagerlyReopen = false;

	constructor(
		private readonly input: HTMLInputElement,
		private readonly getter: (text: string) => Promise<Options>,
		private readonly matcher: (text: string, options: Options) => Options = defaultMatcher,
		private readonly onSelect?: (text: string) => void
	) {
		this.label = input.labels![0];
		this.fieldArea = input.closest('.wp-block-plaudit-query-loop-form-field,.fieldArea') as HTMLFieldSetElement;

		if (!input.hasAttribute("aria-describedby")) {
			const instructionsId = input.id + "__instructions";
			input.insertAdjacentHTML('afterend', `<div id="${instructionsId}" style="display: none">Begin typing to search, use arrow keys to navigate, Enter to select, Escape to toggle the autocomplete results</div>`);
			input.setAttribute("aria-describedby", instructionsId);
		}
		input.spellcheck = false;

		const announcementArea = document.createElement('div');
		announcementArea.classList.add("fieldArea__autocompleteAnnouncementArea", "sr-only");
		announcementArea.ariaLive = "assertive";
		input.parentElement!.appendChild(announcementArea);
		this.announcementArea = announcementArea;

		const results = document.createElement('ul');
		results.id = `${input.id}__autocompleteResults`;
		results.classList.add("fieldArea__autocomplete");
		results.role = "listbox";
		results.setAttribute("aria-labelledby", this.label.id);
		input.parentElement!.appendChild(results);
		this.results = results;

		this.optionIdPrefix = input.id + "__autocompleteResult-"

		input.ariaAutoComplete = "list";
		input.setAttribute("aria-controls", results.id);
		input.ariaHasPopup = "listbox";
		input.ariaExpanded = "false";
		input.autocomplete = "off";

		input.addEventListener('focus', () => {
			this.updateOptions();
			this.expandResults();
		});
		input.addEventListener('focusout', e => {
			if (e.relatedTarget instanceof HTMLElement && e.relatedTarget.parentElement === this.results) {
				this.selectOption();
			}
			this.collapseResults();
			this.setCurrentlyConsideredElement(undefined);
			this.eagerlyReopen = false;
			input.removeAttribute("data-results-explicitly-closed");
		});
		input.addEventListener('keydown', e => {
			if (this.eagerlyReopen && e.key !== 'Enter') {
				this.expandResults();
				this.updateOptions();
				this.eagerlyReopen = false;
			}
			if (e.key === 'ArrowUp') {
				if (this.autocompleteResultsAreVisible()) {
					e.preventDefault();
					if (!this.loading) {
						this.trackingMouse = false;
						if (this.currentlyConsideredElement === undefined) {
							this.setCurrentlyConsideredElement(this.results.lastElementChild as HTMLElement);
						} else {
							this.setCurrentlyConsideredElement((this.currentlyConsideredElement.previousElementSibling ?? this.results.lastElementChild) as HTMLElement);
						}
					}
				}
			} else if (e.key === 'ArrowDown') {
				if (this.autocompleteResultsAreVisible()) {
					e.preventDefault();
					if (!this.loading) {
						this.trackingMouse = false;
						if (this.currentlyConsideredElement === undefined) {
							this.setCurrentlyConsideredElement(this.results.firstElementChild as HTMLElement);
						} else {
							this.setCurrentlyConsideredElement((this.currentlyConsideredElement.nextElementSibling ?? this.results.firstElementChild) as HTMLElement);
						}
					}
				}
			} else if (e.key === 'Escape') {
				e.preventDefault();
				if (this.input.getAttribute("data-results-explicitly-closed") !== "true") {
					this.collapseResults(true);
				} else {
					this.expandResults(true);
				}
			}
		})
		input.addEventListener('keyup', e => {
			switch(e.key) {
				case 'ArrowUp':
				case 'ArrowDown':
					if (this.autocompleteResultsAreVisible()) {
						e.preventDefault();
					}
					break;
				case 'Escape':
					e.preventDefault();
					break;
				case 'Enter':
					if (this.selectOption()) {
						e.preventDefault();
					}
					break;
				// Assume all other cases are printable characters or manipulation keys
				default:
					this.updateOptions();
					break;
			}
		});
		input.addEventListener('keypress', e => {
			if (e.key === 'Backspace' || e.key === 'Delete') {
				this.updateOptions();
			}
		})
	}

	private autocompleteResultsAreVisible() {
		return this.results.computedStyleMap().get("display") !== "none";
	}

	private selectOption(): boolean {
		const currentlyConsideredValue = this.currentlyConsideredElement?.getAttribute("data-value");
		this.collapseResults();
		if (currentlyConsideredValue) {
			if (this.onSelect) {
				this.onSelect(currentlyConsideredValue);
				return true;
			}
			this.eagerlyReopen = true;
			if (currentlyConsideredValue !== this.input.value) {
				this.input.value = currentlyConsideredValue;
			}
			this.input.dispatchEvent(new Event('change', {bubbles: true}));
			return true;
		}
		return false;
	}

	private expandResults(explicit = false) {
		if (explicit) {
			this.input.removeAttribute("data-results-explicitly-closed");
		}
		if (this.input.ariaExpanded === "false" && this.input.getAttribute("data-results-explicitly-closed") !== "true") {
			if (this.results.childElementCount) {
				this.input.ariaExpanded = "true";
			}
			this.fieldArea.classList.toggle("busy", true);
		}
	}
	private collapseResults(explicit = false) {
		if (this.input.ariaExpanded === "true") {
			this.input.ariaExpanded = "false";
			this.fieldArea.classList.toggle("busy", false);
			if (explicit) {
				this.input.setAttribute("data-results-explicitly-closed", "true");
				this.announce("The autocomplete results list has been closed. Press Escape again to reopen it.");
			}
			this.setCurrentlyConsideredElement(undefined);
		}
	}

	private updateOptions() {
		if (this.input.value !== this.updateQueue.lastValue) {
			this.updateQueue.lastValue = this.input.value;

			this.startLoading();
			const positionInLine = ++this.updateQueue.position;
			this.updateQueue.line = this.updateQueue.line.then(() => {
				if (this.updateQueue.position === positionInLine) {
					return this.getter(this.input.value).then(options => {
						if (this.updateQueue.position === positionInLine) {
							this.renderOptions(this.matcher(this.input.value, options));
							this.loading = false;
						}
					});
				}
			});
		}
	}

	private startLoading() {
		if (this.loading) {
			return;
		}

		this.input.ariaExpanded = "true";
		this.fieldArea.classList.toggle("busy", true);
		this.loading = true;
		this.results.innerHTML = "<li aria-disabled='true'>Loading...</li>";
		this.announce("Loading results");
	}

	private announce(announcement: string) {
		this.announcementArea.innerText = announcement;
	}

	private renderOptions(options: Options) {
		const resultElements = options.map((option, index) => {
			const li = document.createElement('li');
			li.setAttribute('role', 'option');
			li.setAttribute('id', this.optionIdPrefix + index);
			li.setAttribute("data-value", option);
			li.tabIndex = -1;
			li.innerHTML = option;

			// Move visual indicator as mouse users hover over option
			li.addEventListener('mouseover', () => {
				if (this.trackingMouse) {
					this.setCurrentlyConsideredElement(li);
				}
			});
			li.addEventListener('mouseout', () => {
				if (this.trackingMouse) {
					this.setCurrentlyConsideredElement(undefined);
				}
			});
			li.addEventListener('mousemove', () => this.trackingMouse = true);
			li.addEventListener('click', () => {
				this.setCurrentlyConsideredElement(li);
				this.selectOption();
				this.input.focus();
				this.input.blur();
				this.eagerlyReopen = true;
			});

			return li;
		});

		const currentlyConsideredValue = this.currentlyConsideredElement?.getAttribute("data-value");
		this.results.replaceChildren(...resultElements);
		this.setCurrentlyConsideredElement(resultElements.find(li => li.getAttribute("data-value") === currentlyConsideredValue));
		this.results.setAttribute("data-count", options.length.toString());
		this.announce(options.length === 1 ? "1 result found." : `${options.length === 0 ? 'No' : options.length} results found.`);
		if (options.length === 0) {
			this.collapseResults();
		} else if (document.activeElement === this.input) {
			this.expandResults();
		}
	}

	private setCurrentlyConsideredElement(element?: HTMLElement) {
		if (element === this.currentlyConsideredElement) {
			return;
		}
		if (this.currentlyConsideredElement !== undefined) {
			this.currentlyConsideredElement.classList.toggle("is-active", false);
		}

		this.currentlyConsideredElement = element;
		if (element === undefined) {
			this.input.removeAttribute('aria-activedescendant');
		} else {
			element.scrollIntoView({behavior: "instant", block: "nearest"});
			element.classList.toggle("is-active", true);
			this.input.setAttribute('aria-activedescendant', element.id);
		}
	}
}
